import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Center,
  FileInput,
  Grid,
  Group,
  Loader,
  Menu,
  Modal,
  Paper,
  ScrollArea,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconArchive,
  IconCheck,
  IconCloudUpload,
  IconDotsVertical,
  IconDownload,
  IconEye,
  IconFileText,
  IconFileTypePdf,
  IconHistory,
  IconInfoCircle,
  IconLayoutGrid,
  IconPrinter,
  IconRefresh,
  IconScissors,
  IconSearch,
  IconSparkles,
  IconTrash,
  IconUpload,
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

import { api, apiOrigin } from '../api';
import {
  calculatePicklist,
  countPdfPages,
  cropShippingLabels,
  cropTaxInvoices,
  DEFAULT_INVOICE_CROP,
  DEFAULT_SHIPPING_CROP,
  downloadBlob,
  extractTextTokens,
  generate2in1Invoices,
  generate4in1A4,
  generateSingleOrderCombinedA4,
  generateThermalLabels,
  mergePdfs,
  type MeeshoInvoiceOption,
  type MeeshoLayoutOption,
  type MeeshoOrderInfo,
  type MeeshoSortOption,
  packageMeeshoZip,
  parseMeeshoMetadataFromTokens,
  type PicklistEntry,
} from '../services/meesho-pdf.service';
import { useAuthStore } from '../stores/auth-store';
import { apiHook } from '../treaty';

interface UploadedFileItem {
  id: string;
  name: string;
  size: number;
  buffer: Uint8Array;
}

export function MeeshoCropperPage() {
  const currentUser = useAuthStore((state) => state.user);
  const queryClient = useQueryClient();

  const [files, setFiles] = useState<UploadedFileItem[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingSamples, setIsLoadingSamples] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [layout, setLayout] = useState<MeeshoLayoutOption>('4in1');
  const [invoiceOption, setInvoiceOption] = useState<MeeshoInvoiceOption>('separate_2in1');
  const [includeCutMarks, setIncludeCutMarks] = useState(true);
  const [rotate90, setRotate90] = useState(true);
  const [sortBy, setSortBy] = useState<MeeshoSortOption>('default');
  const [activeTab, setActiveTab] = useState<string | null>('preview');
  const [previewMode, setPreviewMode] = useState<'shipping' | 'invoice' | 'combined'>('shipping');

  const [saveModalOpened, { open: openSaveModal, close: closeSaveModal }] = useDisclosure(false);
  const [batchNameInput, setBatchNameInput] = useState('');
  const [historySearch, setHistorySearch] = useState('');

  const [shippingPdfBlobUrl, setShippingPdfBlobUrl] = useState<string | null>(null);
  const [invoicePdfBlobUrl, setInvoicePdfBlobUrl] = useState<string | null>(null);
  const [combinedPdfBlobUrl, setCombinedPdfBlobUrl] = useState<string | null>(null);
  const [shippingPdfBytes, setShippingPdfBytes] = useState<Uint8Array | null>(null);
  const [invoicePdfBytes, setInvoicePdfBytes] = useState<Uint8Array | null>(null);
  const [combinedPdfBytes, setCombinedPdfBytes] = useState<Uint8Array | null>(null);
  const [parsedOrders, setParsedOrders] = useState<MeeshoOrderInfo[]>([]);

  // Query saved batches from server
  const { data: batchesData, isLoading: isLoadingBatches } = apiHook.useQuery((client) =>
    client.meesho.batches.get(),
  );
  const savedBatches = batchesData?.batches ?? [];

  const fileInputRef = useRef<HTMLButtonElement>(null);

  // Clean up blob URLs on unmount or re-render
  useEffect(() => {
    return () => {
      if (shippingPdfBlobUrl) URL.revokeObjectURL(shippingPdfBlobUrl);
      if (invoicePdfBlobUrl) URL.revokeObjectURL(invoicePdfBlobUrl);
      if (combinedPdfBlobUrl) URL.revokeObjectURL(combinedPdfBlobUrl);
    };
  }, [shippingPdfBlobUrl, invoicePdfBlobUrl, combinedPdfBlobUrl]);

  // Process files whenever files, layout, invoiceOption, includeCutMarks, rotate90, or sortBy changes
  useEffect(() => {
    if (files.length === 0) {
      setShippingPdfBlobUrl(null);
      setInvoicePdfBlobUrl(null);
      setShippingPdfBytes(null);
      setInvoicePdfBytes(null);
      setParsedOrders([]);
      return;
    }

    let isMounted = true;
    async function processPdfs() {
      setIsProcessing(true);
      try {
        const rawBuffers: Uint8Array[] = [];
        const orders: MeeshoOrderInfo[] = [];

        let globalIndex = 0;
        for (const f of files) {
          const tokens = extractTextTokens(f.buffer);
          const meta = parseMeeshoMetadataFromTokens(tokens, globalIndex, f.name);
          orders.push(meta);
          rawBuffers.push(f.buffer);
          globalIndex++;
        }

        const pageCount = await countPdfPages(rawBuffers);

        // Apply sorting
        if (sortBy === 'sku') {
          orders.sort((a, b) => a.sku.localeCompare(b.sku));
        } else if (sortBy === 'courier') {
          orders.sort((a, b) => a.courier.localeCompare(b.courier));
        } else if (sortBy === 'customer') {
          orders.sort((a, b) => a.customerName.localeCompare(b.customerName));
        }

        // 1. Generate Shipping Labels PDF
        let shipBytes: Uint8Array;
        if (layout === '4in1') {
          shipBytes = await generate4in1A4(rawBuffers, {
            includeCutMarks,
            rotate90,
            bounds: DEFAULT_SHIPPING_CROP,
          });
        } else if (layout === 'thermal_landscape') {
          shipBytes = await generateThermalLabels(rawBuffers, 'landscape', DEFAULT_SHIPPING_CROP);
        } else if (layout === 'thermal_portrait') {
          shipBytes = await generateThermalLabels(rawBuffers, 'portrait', DEFAULT_SHIPPING_CROP);
        } else {
          shipBytes = await cropShippingLabels(rawBuffers, DEFAULT_SHIPPING_CROP);
        }

        // 2. Generate Tax Invoices PDF
        let invBytes: Uint8Array | null = null;
        if (invoiceOption === 'separate_2in1') {
          invBytes = await generate2in1Invoices(rawBuffers, {
            includeCutMarks,
            bounds: DEFAULT_INVOICE_CROP,
          });
        } else if (invoiceOption === 'separate_1in1') {
          invBytes = await cropTaxInvoices(rawBuffers, DEFAULT_INVOICE_CROP);
        }

        // 3. Generate Combined PDF (Shipping Labels + Invoices)
        let combBytes: Uint8Array | null = null;
        if (invBytes && invoiceOption !== 'skip') {
          combBytes =
            pageCount === 1 && layout === '4in1' && invoiceOption === 'separate_2in1'
              ? await generateSingleOrderCombinedA4(rawBuffers, {
                  includeCutMarks,
                  rotateShipping90: rotate90,
                })
              : await mergePdfs([shipBytes, invBytes]);
        }

        if (isMounted) {
          setParsedOrders(orders);
          setShippingPdfBytes(shipBytes);
          setInvoicePdfBytes(invBytes);
          setCombinedPdfBytes(combBytes);

          // Update Blob URLs
          if (shippingPdfBlobUrl) URL.revokeObjectURL(shippingPdfBlobUrl);
          if (invoicePdfBlobUrl) URL.revokeObjectURL(invoicePdfBlobUrl);
          if (combinedPdfBlobUrl) URL.revokeObjectURL(combinedPdfBlobUrl);

          const newShipUrl = URL.createObjectURL(
            new Blob([shipBytes as unknown as BlobPart], { type: 'application/pdf' }),
          );
          setShippingPdfBlobUrl(newShipUrl);

          if (invBytes) {
            const newInvUrl = URL.createObjectURL(
              new Blob([invBytes as unknown as BlobPart], { type: 'application/pdf' }),
            );
            setInvoicePdfBlobUrl(newInvUrl);
          } else {
            setInvoicePdfBlobUrl(null);
          }

          if (combBytes) {
            const newCombUrl = URL.createObjectURL(
              new Blob([combBytes as unknown as BlobPart], { type: 'application/pdf' }),
            );
            setCombinedPdfBlobUrl(newCombUrl);
          } else {
            setCombinedPdfBlobUrl(null);
          }
        }
      } catch (err) {
        console.error('Failed to process Meesho PDFs:', err);
        notifications.show({
          title: 'Processing Error',
          message: 'Failed to crop one or more PDF files. Please ensure valid Meesho label PDFs.',
          color: 'red',
        });
      } finally {
        if (isMounted) setIsProcessing(false);
      }
    }

    void processPdfs();

    return () => {
      isMounted = false;
    };
  }, [files, layout, invoiceOption, includeCutMarks, rotate90, sortBy]);

  // Picklist summary
  const picklist: PicklistEntry[] = useMemo(() => {
    return calculatePicklist(parsedOrders);
  }, [parsedOrders]);

  // Statistics
  const totalOrders = parsedOrders.length;
  const totalSheetsNeeded = layout === '4in1' ? Math.ceil(totalOrders / 4) : totalOrders;
  const standardSheetsWithoutCrop = totalOrders;
  const sheetsSaved =
    layout === '4in1' ? Math.max(0, standardSheetsWithoutCrop - totalSheetsNeeded) : 0;
  const paperSavedPercentage =
    layout === '4in1' && standardSheetsWithoutCrop > 0
      ? Math.round((sheetsSaved / standardSheetsWithoutCrop) * 100)
      : 0;

  // Handle file uploads
  const handleFilesSelected = async (selectedFiles: File[]) => {
    if (!selectedFiles || selectedFiles.length === 0) return;

    const newItems: UploadedFileItem[] = [];
    for (const f of selectedFiles) {
      if (f.type === 'application/pdf' || f.name.endsWith('.pdf')) {
        const arrayBuf = await f.arrayBuffer();
        newItems.push({
          id: `${f.name}-${f.size}-${Date.now()}-${Math.random()}`,
          name: f.name,
          size: f.size,
          buffer: new Uint8Array(arrayBuf),
        });
      }
    }

    if (newItems.length === 0) {
      notifications.show({
        title: 'No valid PDF files',
        message: 'Please upload files in PDF format.',
        color: 'yellow',
      });
      return;
    }

    setFiles((prev) => [...prev, ...newItems]);
    notifications.show({
      title: 'Files Uploaded',
      message: `Added ${newItems.length} Meesho PDF file(s) for processing.`,
      color: 'blue',
    });
  };

  // Load sample Meesho files
  const loadSampleLabels = async () => {
    setIsLoadingSamples(true);
    try {
      const res = await api.api.api.v1.meesho.samples.get();
      if (res.data?.samples && res.data.samples.length > 0) {
        const sampleItems: UploadedFileItem[] = (
          res.data.samples as { name: string; size: number; base64: string }[]
        ).map((s) => {
          const binaryStr = atob(s.base64);
          const bytes = new Uint8Array(binaryStr.length);
          for (let i = 0; i < binaryStr.length; i++) {
            bytes[i] = binaryStr.charCodeAt(i);
          }
          return {
            id: `sample-${s.name}-${Date.now()}`,
            name: s.name,
            size: s.size,
            buffer: bytes,
          };
        });

        setFiles(sampleItems);
        notifications.show({
          title: 'Sample Labels Loaded',
          message: `Loaded ${sampleItems.length} sample Meesho labels (${sampleItems.map((s) => s.name).join(', ')}).`,
          color: 'teal',
          icon: <IconCheck size={18} />,
        });
      } else {
        notifications.show({
          title: 'Samples Not Available',
          message: 'Could not find sample files on server.',
          color: 'orange',
        });
      }
    } catch (err) {
      console.error('Error loading samples:', err);
      notifications.show({
        title: 'Error loading samples',
        message: 'Could not fetch sample files from server.',
        color: 'red',
      });
    } finally {
      setIsLoadingSamples(false);
    }
  };

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const clearAllFiles = () => {
    setFiles([]);
  };

  // Download actions
  const handleDownloadShipping = () => {
    if (!shippingPdfBytes) return;
    const suffix =
      layout === '4in1' ? '4in1_A4' : layout === 'thermal_landscape' ? 'thermal_6x4' : 'cropped';
    downloadBlob(shippingPdfBytes, `meesho_shipping_labels_${suffix}.pdf`);
    notifications.show({
      title: 'Download Started',
      message: 'Shipping labels PDF is downloading.',
      color: 'blue',
    });
  };

  const handleDownloadInvoices = () => {
    if (!invoicePdfBytes) {
      notifications.show({
        title: 'No Invoices',
        message: 'Invoice generation is set to skip or not available.',
        color: 'orange',
      });
      return;
    }
    downloadBlob(invoicePdfBytes, 'meesho_tax_invoices.pdf');
    notifications.show({
      title: 'Download Started',
      message: 'Tax Invoices PDF is downloading.',
      color: 'blue',
    });
  };

  const handleDownloadZip = async () => {
    if (!shippingPdfBytes) return;
    try {
      const zipBytes = await packageMeeshoZip(shippingPdfBytes, invoicePdfBytes || undefined);
      downloadBlob(zipBytes, 'meesho_labels_and_invoices.zip', 'application/zip');
      notifications.show({
        title: 'ZIP Archive Downloaded',
        message: 'Both shipping labels and tax invoices packaged in ZIP.',
        color: 'teal',
        icon: <IconCheck size={18} />,
      });
    } catch (err) {
      console.error('ZIP packaging failed:', err);
      notifications.show({
        title: 'ZIP Packaging Failed',
        message: 'Could not generate ZIP archive.',
        color: 'red',
      });
    }
  };

  const handleDownloadCombined = () => {
    if (!combinedPdfBytes) {
      notifications.show({
        title: 'No Combined PDF',
        message: 'Could not generate combined PDF (check if invoices are skipped).',
        color: 'orange',
      });
      return;
    }
    const suffix =
      layout === '4in1' ? '4in1_A4' : layout === 'thermal_landscape' ? 'thermal_6x4' : 'cropped';
    downloadBlob(combinedPdfBytes, `meesho_combined_labels_and_invoices_${suffix}.pdf`);
    notifications.show({
      title: 'Download Started',
      message: 'Combined PDF (Shipping Labels + Tax Invoices) is downloading.',
      color: 'teal',
      icon: <IconCheck size={18} />,
    });
  };

  const handleDirectPrint = () => {
    const urlToPrint =
      previewMode === 'shipping'
        ? shippingPdfBlobUrl
        : previewMode === 'invoice'
          ? invoicePdfBlobUrl
          : combinedPdfBlobUrl;
    if (!urlToPrint) return;
    const printWindow = window.open(urlToPrint);
    if (printWindow) {
      printWindow.focus();
    }
  };

  const getSuggestedBatchName = () => {
    const emailPrefix = currentUser?.email?.split('@')[0] || 'User';
    const displayName = emailPrefix.toLowerCase().includes('patoliadixit')
      ? 'Dixit Patolia'
      : emailPrefix.replace(/[._-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

    const now = new Date();
    const dateFormatted = now.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    const timeFormatted = now.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });

    const layoutLabel =
      layout === '4in1'
        ? '4-in-1 A4'
        : layout === 'thermal_landscape'
          ? 'Thermal 6x4'
          : layout === 'thermal_portrait'
            ? 'Thermal 4x6'
            : 'Single Cropped';

    return `${displayName} - ${dateFormatted}, ${timeFormatted} (${totalOrders} Orders - ${layoutLabel})`;
  };

  const handleSaveBatch = async () => {
    if (files.length === 0) return;
    setIsSaving(true);
    try {
      const payloadFiles = files.map((f) => {
        let binary = '';
        const len = f.buffer.byteLength;
        for (let i = 0; i < len; i++) {
          binary += String.fromCharCode(f.buffer[i]);
        }
        return {
          name: f.name,
          base64: btoa(binary),
        };
      });

      const res = await api.api.api.v1.meesho.batches.post({
        name: batchNameInput.trim() || undefined,
        layout,
        invoiceOption,
        includeCutMarks,
        rotate90,
        sortBy,
        files: payloadFiles,
      });

      if (res.error) {
        notifications.show({
          title: 'Save Failed',
          message: 'Could not save the batch to the server.',
          color: 'red',
        });
        return;
      }

      await queryClient.invalidateQueries({ queryKey: ['meesho', 'batches'] });
      closeSaveModal();
      notifications.show({
        title: 'Batch Saved to Server',
        message: `"${res.data?.batch?.name}" saved successfully!`,
        color: 'teal',
        icon: <IconCheck size={18} />,
      });
      setActiveTab('history');
    } catch (err) {
      console.error('Save error:', err);
      notifications.show({
        title: 'Save Error',
        message: 'An unexpected error occurred while saving the batch.',
        color: 'red',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownloadSavedFile = (
    batchId: string,
    type: 'shipping' | 'invoice' | 'combined' | 'zip',
  ) => {
    const url = `${apiOrigin}/api/v1/meesho/batches/${batchId}/download/${type}`;
    window.open(url, '_blank');
  };

  const handleLoadSavedBatch = async (batchId: string) => {
    try {
      const res = await api.api.api.v1.meesho.batches({ id: batchId }).get();
      if (res.data?.batch) {
        const b = res.data.batch;

        const shipRes = await fetch(
          `${apiOrigin}/api/v1/meesho/batches/${batchId}/download/shipping`,
          { headers: api.authorizationHeaders },
        );
        const shipArrayBuf = await shipRes.arrayBuffer();
        const shipBytes = new Uint8Array(shipArrayBuf);

        let invBytes: Uint8Array | null = null;
        if (b.hasInvoices) {
          const invRes = await fetch(
            `${apiOrigin}/api/v1/meesho/batches/${batchId}/download/invoice`,
            { headers: api.authorizationHeaders },
          );
          const invArrayBuf = await invRes.arrayBuffer();
          invBytes = new Uint8Array(invArrayBuf);
        }

        let combBytes: Uint8Array | null = null;
        if (b.hasCombined) {
          const combRes = await fetch(
            `${apiOrigin}/api/v1/meesho/batches/${batchId}/download/combined`,
            { headers: api.authorizationHeaders },
          );
          const combArrayBuf = await combRes.arrayBuffer();
          combBytes = new Uint8Array(combArrayBuf);
        }

        setLayout(b.layout as MeeshoLayoutOption);
        setInvoiceOption(b.invoiceOption as MeeshoInvoiceOption);
        setIncludeCutMarks(b.includeCutMarks);
        setRotate90(b.rotate90);
        setParsedOrders(b.orders as MeeshoOrderInfo[]);
        setShippingPdfBytes(shipBytes);
        setInvoicePdfBytes(invBytes);
        setCombinedPdfBytes(combBytes);

        if (shippingPdfBlobUrl) URL.revokeObjectURL(shippingPdfBlobUrl);
        if (invoicePdfBlobUrl) URL.revokeObjectURL(invoicePdfBlobUrl);
        if (combinedPdfBlobUrl) URL.revokeObjectURL(combinedPdfBlobUrl);

        setShippingPdfBlobUrl(
          URL.createObjectURL(
            new Blob([shipBytes as unknown as BlobPart], { type: 'application/pdf' }),
          ),
        );
        if (invBytes) {
          setInvoicePdfBlobUrl(
            URL.createObjectURL(
              new Blob([invBytes as unknown as BlobPart], { type: 'application/pdf' }),
            ),
          );
        } else {
          setInvoicePdfBlobUrl(null);
        }
        if (combBytes) {
          setCombinedPdfBlobUrl(
            URL.createObjectURL(
              new Blob([combBytes as unknown as BlobPart], { type: 'application/pdf' }),
            ),
          );
        } else {
          setCombinedPdfBlobUrl(null);
        }

        setActiveTab('preview');
        notifications.show({
          title: 'Batch Loaded to Preview',
          message: `Loaded "${b.name}" with ${b.orderCount} orders.`,
          color: 'teal',
          icon: <IconCheck size={18} />,
        });
      }
    } catch (err) {
      console.error('Load saved batch error:', err);
      notifications.show({
        title: 'Load Error',
        message: 'Could not load batch from server.',
        color: 'red',
      });
    }
  };

  const handleDeleteSavedBatch = async (batchId: string, batchName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${batchName}" from the server?`)) return;
    try {
      const res = await api.api.api.v1.meesho.batches({ id: batchId }).delete();
      if (res.error) {
        notifications.show({
          title: 'Delete Failed',
          message: 'Could not delete batch from server.',
          color: 'red',
        });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['meesho', 'batches'] });
      notifications.show({
        title: 'Batch Deleted',
        message: `Deleted "${batchName}".`,
        color: 'gray',
      });
    } catch (err) {
      console.error('Delete error:', err);
      notifications.show({
        title: 'Delete Error',
        message: 'Could not delete batch.',
        color: 'red',
      });
    }
  };

  const filteredBatches = useMemo(() => {
    if (!historySearch.trim()) return savedBatches;
    const query = historySearch.toLowerCase();
    return savedBatches.filter((b) => {
      const nameMatch = b.name.toLowerCase().includes(query);
      const creatorMatch = b.createdBy?.email.toLowerCase().includes(query);
      const skuMatch = b.orders?.some(
        (o: MeeshoOrderInfo) =>
          o.sku.toLowerCase().includes(query) ||
          o.customerName.toLowerCase().includes(query) ||
          o.orderNo.toLowerCase().includes(query) ||
          o.courier.toLowerCase().includes(query),
      );
      return nameMatch || creatorMatch || skuMatch;
    });
  }, [savedBatches, historySearch]);

  return (
    <Stack gap='lg'>
      {/* Header */}
      <Paper p='lg' radius='md' withBorder className='data-card'>
        <Group justify='space-between' align='flex-start' wrap='wrap' gap='md'>
          <Stack gap={4}>
            <Group gap='xs'>
              <Box className='brand-mark' style={{ width: 34, height: 34 }}>
                <IconScissors size={20} />
              </Box>
              <Title order={2}>Meesho Label Cropper</Title>
              <Badge color='blue' variant='light' size='md'>
                4-in-1 A4 & Thermal 4x6
              </Badge>
            </Group>
            <Text c='dimmed' size='sm'>
              Crop Meesho bulk shipping labels and tax invoices, fit 4 shipping labels on 1 A4 sheet
              (75% paper savings), or format for thermal barcode printers.
            </Text>
          </Stack>

          <Group gap='xs'>
            <Button
              variant='light'
              color='teal'
              leftSection={<IconSparkles size={18} />}
              onClick={() => void loadSampleLabels()}
              loading={isLoadingSamples}
            >
              Load Sample Labels
            </Button>
            {files.length > 0 && (
              <Button
                variant='subtle'
                color='red'
                leftSection={<IconTrash size={18} />}
                onClick={clearAllFiles}
              >
                Clear All
              </Button>
            )}
          </Group>
        </Group>
      </Paper>

      {/* Upload Zone & Settings */}
      <Grid gap='md'>
        {/* Left Column: Upload Dropzone & File List */}
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Stack gap='md'>
            <Paper p='md' radius='md' withBorder>
              <Stack gap='sm'>
                <Group justify='space-between'>
                  <Text fw={600} size='sm'>
                    Upload Meesho PDFs
                  </Text>
                  {files.length > 0 && (
                    <Badge color='blue' variant='filled' size='sm'>
                      {files.length} file{files.length > 1 ? 's' : ''} ({totalOrders} orders)
                    </Badge>
                  )}
                </Group>

                <FileInput
                  ref={fileInputRef}
                  placeholder='Drag & drop Meesho label PDFs here or click to browse'
                  leftSection={<IconUpload size={18} />}
                  accept='application/pdf'
                  multiple
                  onChange={(payload) => void handleFilesSelected(payload)}
                  clearable
                />

                <Text size='xs' c='dimmed'>
                  Supports single & bulk multi-order PDFs directly downloaded from Meesho Seller
                  Panel.
                </Text>
              </Stack>
            </Paper>

            {/* Uploaded Files List */}
            {files.length > 0 && (
              <Paper p='sm' radius='md' withBorder>
                <Text fw={600} size='xs' c='dimmed' mb='xs' tt='uppercase'>
                  Loaded Files ({files.length})
                </Text>
                <ScrollArea h={160}>
                  <Stack gap='xs'>
                    {files.map((file, idx) => (
                      <Group
                        key={file.id}
                        justify='space-between'
                        p='xs'
                        style={{
                          background: 'var(--mantine-color-gray-0)',
                          borderRadius: 'var(--mantine-radius-sm)',
                        }}
                      >
                        <Group gap='xs' style={{ maxWidth: '80%', overflow: 'hidden' }}>
                          <IconFileTypePdf size={20} color='var(--mantine-color-red-6)' />
                          <Stack gap={1} style={{ overflow: 'hidden' }}>
                            <Text size='xs' fw={600} truncate title={file.name}>
                              {idx + 1}. {file.name}
                            </Text>
                            <Text size='10px' c='dimmed'>
                              {(file.size / 1024).toFixed(1)} KB
                            </Text>
                          </Stack>
                        </Group>
                        <ActionIcon
                          size='sm'
                          color='gray'
                          variant='subtle'
                          onClick={() => removeFile(file.id)}
                          title='Remove file'
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    ))}
                  </Stack>
                </ScrollArea>
              </Paper>
            )}

            {/* Layout Configuration */}
            <Paper p='md' radius='md' withBorder>
              <Stack gap='md'>
                <Text fw={600} size='sm'>
                  Output Formatting Options
                </Text>

                <Stack gap='xs'>
                  <Text size='xs' fw={500}>
                    Shipping Label Layout
                  </Text>
                  <SegmentedControl
                    fullWidth
                    size='xs'
                    value={layout}
                    onChange={(val) => setLayout(val as MeeshoLayoutOption)}
                    data={[
                      { label: '4-in-1 A4', value: '4in1' },
                      { label: 'Thermal 6x4', value: 'thermal_landscape' },
                      { label: 'Thermal 4x6', value: 'thermal_portrait' },
                      { label: 'Single Cropped', value: 'single_cropped' },
                    ]}
                  />
                  <Text size='11px' c='dimmed'>
                    {layout === '4in1'
                      ? '4 labels per A4 page in 2x2 grid for standard desktop/laser printers.'
                      : layout === 'thermal_landscape'
                        ? '6x4 inch format for standard horizontal thermal barcode sticker rolls.'
                        : layout === 'thermal_portrait'
                          ? '4x6 inch format rotated for vertical thermal label printers.'
                          : 'Exact cropped shipping label bounding box.'}
                  </Text>
                </Stack>

                <Stack gap='xs'>
                  <Text size='xs' fw={500}>
                    Tax Invoices Output
                  </Text>
                  <Select
                    size='xs'
                    value={invoiceOption}
                    onChange={(val) =>
                      setInvoiceOption((val as MeeshoInvoiceOption) || 'separate_2in1')
                    }
                    data={[
                      { label: '2 Invoices per A4 sheet (Paper saver)', value: 'separate_2in1' },
                      { label: '1 Invoice per page (Full page cropped)', value: 'separate_1in1' },
                      { label: 'Skip invoices (Shipping labels only)', value: 'skip' },
                    ]}
                  />
                </Stack>

                <Group justify='space-between'>
                  <Stack gap={2}>
                    <Text size='xs' fw={500}>
                      Cut Guidelines (Dashed Lines)
                    </Text>
                    <Text size='10px' c='dimmed'>
                      Shows cutting marks on multi-label A4 pages
                    </Text>
                  </Stack>
                  <Switch
                    checked={includeCutMarks}
                    onChange={(e) => setIncludeCutMarks(e.currentTarget.checked)}
                    disabled={layout !== '4in1' && invoiceOption !== 'separate_2in1'}
                  />
                </Group>

                <Group justify='space-between'>
                  <Stack gap={2}>
                    <Text size='xs' fw={500}>
                      Rotate 90° (Maximize Label Size)
                    </Text>
                    <Text size='10px' c='dimmed'>
                      Rotates shipping labels to completely fill each A4 quadrant
                    </Text>
                  </Stack>
                  <Switch
                    checked={rotate90}
                    onChange={(e) => setRotate90(e.currentTarget.checked)}
                    disabled={layout !== '4in1'}
                  />
                </Group>

                <Stack gap='xs'>
                  <Text size='xs' fw={500}>
                    Order Sorting (Batch Packing)
                  </Text>
                  <Select
                    size='xs'
                    value={sortBy}
                    onChange={(val) => setSortBy((val as MeeshoSortOption) || 'default')}
                    data={[
                      { label: 'Default (Upload Order)', value: 'default' },
                      { label: 'Sort by SKU (Group identical products)', value: 'sku' },
                      { label: 'Sort by Courier (Group by delivery partner)', value: 'courier' },
                      { label: 'Sort by Customer Name', value: 'customer' },
                    ]}
                  />
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        </Grid.Col>

        {/* Right Column: Metrics, Action Buttons, and Live Preview */}
        <Grid.Col span={{ base: 12, md: 7 }}>
          <Stack gap='md'>
            {/* Stats Cards */}
            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing='sm'>
              <Card p='sm' radius='md' withBorder>
                <Text size='xs' c='dimmed' fw={600}>
                  TOTAL ORDERS
                </Text>
                <Text size='xl' fw={700} c='blue.7'>
                  {totalOrders}
                </Text>
                <Text size='10px' c='dimmed'>
                  {files.length} PDF file{files.length !== 1 ? 's' : ''}
                </Text>
              </Card>

              <Card p='sm' radius='md' withBorder>
                <Text size='xs' c='dimmed' fw={600}>
                  A4 SHEETS
                </Text>
                <Text size='xl' fw={700} c='teal.7'>
                  {totalSheetsNeeded}
                </Text>
                <Text size='10px' c='dimmed'>
                  {layout === '4in1' ? '4 labels / sheet' : '1 label / page'}
                </Text>
              </Card>

              <Card p='sm' radius='md' withBorder>
                <Text size='xs' c='dimmed' fw={600}>
                  PAPER SAVED
                </Text>
                <Group gap={6} align='center'>
                  <Text size='xl' fw={700} c='green.7'>
                    {paperSavedPercentage}%
                  </Text>
                  {sheetsSaved > 0 && (
                    <Badge color='green' size='xs' variant='light'>
                      -{sheetsSaved} sheets
                    </Badge>
                  )}
                </Group>
                <Text size='10px' c='dimmed'>
                  vs. uncropped A4 printing
                </Text>
              </Card>

              <Card p='sm' radius='md' withBorder>
                <Text size='xs' c='dimmed' fw={600}>
                  UNIQUE SKUS
                </Text>
                <Text size='xl' fw={700} c='indigo.7'>
                  {picklist.length}
                </Text>
                <Text size='10px' c='dimmed'>
                  in batch
                </Text>
              </Card>
            </SimpleGrid>
            {/* Quick Action Buttons */}
            <Paper p='sm' radius='md' withBorder bg='blue.0'>
              <Group justify='space-between' wrap='wrap' gap='xs'>
                <Group gap='xs' wrap='wrap'>
                  <Button
                    color='indigo'
                    variant='filled'
                    leftSection={<IconCloudUpload size={18} />}
                    onClick={() => {
                      setBatchNameInput(getSuggestedBatchName());
                      openSaveModal();
                    }}
                    disabled={!shippingPdfBytes || isProcessing || files.length === 0}
                    loading={isSaving}
                  >
                    Save Batch to Server
                  </Button>

                  {invoiceOption !== 'skip' && combinedPdfBytes && (
                    <Button
                      color='teal'
                      variant='light'
                      leftSection={<IconDownload size={18} />}
                      onClick={handleDownloadCombined}
                      disabled={!combinedPdfBytes || isProcessing}
                      loading={isProcessing}
                    >
                      Combined PDF
                    </Button>
                  )}

                  <Button
                    color='blue'
                    variant='light'
                    leftSection={<IconDownload size={18} />}
                    onClick={handleDownloadShipping}
                    disabled={!shippingPdfBytes || isProcessing}
                    loading={isProcessing}
                  >
                    Shipping Labels ({layout === '4in1' ? '4-in-1 A4' : 'Thermal'})
                  </Button>

                  {invoiceOption !== 'skip' && (
                    <Button
                      variant='light'
                      color='indigo'
                      leftSection={<IconFileText size={18} />}
                      onClick={handleDownloadInvoices}
                      disabled={!invoicePdfBytes || isProcessing}
                    >
                      Invoices Only
                    </Button>
                  )}

                  <Button
                    variant='outline'
                    color='gray'
                    leftSection={<IconArchive size={18} />}
                    onClick={() => void handleDownloadZip()}
                    disabled={!shippingPdfBytes || isProcessing}
                    title='Download ZIP with separated labels and invoices'
                  >
                    ZIP
                  </Button>
                </Group>

                <Button
                  variant='subtle'
                  color='blue'
                  leftSection={<IconPrinter size={18} />}
                  onClick={handleDirectPrint}
                  disabled={!shippingPdfBytes || isProcessing}
                >
                  Print
                </Button>
              </Group>
            </Paper>

            {/* Main Tabs Area: Preview, Orders List, Picklist, Saved Batches History */}
            <Paper p='md' radius='md' withBorder style={{ minHeight: 480 }}>
              <Tabs value={activeTab} onChange={setActiveTab}>
                <Tabs.List mb='md'>
                  <Tabs.Tab value='preview' leftSection={<IconEye size={16} />}>
                    Visual PDF Preview
                  </Tabs.Tab>
                  <Tabs.Tab
                    value='orders'
                    leftSection={<IconLayoutGrid size={16} />}
                    rightSection={
                      totalOrders > 0 ? (
                        <Badge size='xs' variant='filled' color='gray'>
                          {totalOrders}
                        </Badge>
                      ) : null
                    }
                  >
                    Orders & Metadata
                  </Tabs.Tab>
                  <Tabs.Tab
                    value='picklist'
                    leftSection={<IconFileText size={16} />}
                    rightSection={
                      picklist.length > 0 ? (
                        <Badge size='xs' variant='filled' color='teal'>
                          {picklist.length}
                        </Badge>
                      ) : null
                    }
                  >
                    Warehouse Picklist
                  </Tabs.Tab>
                  <Tabs.Tab
                    value='history'
                    leftSection={<IconHistory size={16} />}
                    rightSection={
                      savedBatches.length > 0 ? (
                        <Badge size='xs' variant='filled' color='indigo'>
                          {savedBatches.length}
                        </Badge>
                      ) : null
                    }
                  >
                    Saved Batches History
                  </Tabs.Tab>
                </Tabs.List>

                {/* Tab 1: Live PDF Visual Preview */}
                <Tabs.Panel value='preview'>
                  <Stack gap='sm'>
                    {files.length === 0 ? (
                      <Center p='xl' style={{ height: 360, flexDirection: 'column', gap: 12 }}>
                        <IconFileTypePdf size={48} color='var(--mantine-color-gray-4)' />
                        <Text fw={600} c='dimmed'>
                          No PDF files uploaded yet
                        </Text>
                        <Text size='xs' c='dimmed' maw={320} ta='center'>
                          Upload your Meesho label PDFs using the dropzone on the left or click
                          &quot;Load Sample Labels&quot; to preview.
                        </Text>
                        <Button
                          variant='light'
                          color='teal'
                          size='xs'
                          leftSection={<IconSparkles size={14} />}
                          onClick={() => void loadSampleLabels()}
                          loading={isLoadingSamples}
                        >
                          Load Samples Now
                        </Button>
                      </Center>
                    ) : isProcessing ? (
                      <Center p='xl' style={{ height: 360, flexDirection: 'column', gap: 12 }}>
                        <Loader color='blue' size='lg' />
                        <Text size='sm' c='dimmed' fw={500}>
                          Cropping and rendering Meesho labels...
                        </Text>
                      </Center>
                    ) : (
                      <Stack gap='xs'>
                        <Group justify='space-between' wrap='wrap'>
                          <SegmentedControl
                            size='xs'
                            value={previewMode}
                            onChange={(val) =>
                              setPreviewMode(val as 'shipping' | 'invoice' | 'combined')
                            }
                            data={[
                              { label: `Shipping Labels (${layout})`, value: 'shipping' },
                              {
                                label: `Tax Invoices (${invoiceOption === 'skip' ? 'Off' : invoiceOption})`,
                                value: 'invoice',
                                disabled: invoiceOption === 'skip' || !invoicePdfBlobUrl,
                              },
                              {
                                label: 'Combined (Labels + Invoices)',
                                value: 'combined',
                                disabled: invoiceOption === 'skip' || !combinedPdfBlobUrl,
                              },
                            ]}
                          />
                          <Group gap='xs'>
                            {previewMode === 'shipping' && (
                              <Badge size='sm' variant='light' color='blue'>
                                {layout === '4in1'
                                  ? `${totalSheetsNeeded} A4 sheet(s) (${rotate90 ? 'Rotated 90°' : 'Portrait'})`
                                  : `${totalOrders} thermal label(s)`}
                              </Badge>
                            )}
                            {previewMode === 'invoice' && (
                              <Badge size='sm' variant='light' color='indigo'>
                                {invoiceOption === 'separate_2in1'
                                  ? `${Math.ceil(totalOrders / 2)} A4 sheet(s) (2-in-1)`
                                  : `${totalOrders} sheet(s)`}
                              </Badge>
                            )}
                            {previewMode === 'combined' && (
                              <Badge size='sm' variant='light' color='teal'>
                                {totalSheetsNeeded +
                                  (invoiceOption === 'separate_2in1'
                                    ? Math.ceil(totalOrders / 2)
                                    : totalOrders)}{' '}
                                total pages
                              </Badge>
                            )}
                          </Group>
                        </Group>

                        {/* Responsive PDF Viewer */}
                        <Box
                          style={{
                            height: 600,
                            border: '1px solid var(--mantine-color-gray-3)',
                            borderRadius: 'var(--mantine-radius-md)',
                            overflow: 'hidden',
                            backgroundColor: 'var(--mantine-color-gray-1)',
                          }}
                        >
                          {previewMode === 'shipping' && shippingPdfBlobUrl ? (
                            <iframe
                              src={shippingPdfBlobUrl}
                              title='Shipping Labels Preview'
                              style={{ width: '100%', height: '100%', border: 0 }}
                            />
                          ) : previewMode === 'invoice' && invoicePdfBlobUrl ? (
                            <iframe
                              src={invoicePdfBlobUrl}
                              title='Tax Invoices Preview'
                              style={{ width: '100%', height: '100%', border: 0 }}
                            />
                          ) : previewMode === 'combined' && combinedPdfBlobUrl ? (
                            <iframe
                              src={combinedPdfBlobUrl}
                              title='Combined Labels and Invoices Preview'
                              style={{ width: '100%', height: '100%', border: 0 }}
                            />
                          ) : (
                            <Center h='100%'>
                              <Text size='sm' c='dimmed'>
                                Preview disabled or not generated.
                              </Text>
                            </Center>
                          )}
                        </Box>
                      </Stack>
                    )}
                  </Stack>
                </Tabs.Panel>

                {/* Tab 2: Orders Manifest Table */}
                <Tabs.Panel value='orders'>
                  {parsedOrders.length === 0 ? (
                    <Center p='xl' style={{ height: 320 }}>
                      <Text c='dimmed' size='sm'>
                        Upload Meesho PDFs to see order manifest.
                      </Text>
                    </Center>
                  ) : (
                    <ScrollArea h={480}>
                      <Table striped highlightOnHover withTableBorder>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>#</Table.Th>
                            <Table.Th>Customer</Table.Th>
                            <Table.Th>Courier</Table.Th>
                            <Table.Th>Dest. Code</Table.Th>
                            <Table.Th>SKU</Table.Th>
                            <Table.Th>Qty</Table.Th>
                            <Table.Th>Order No.</Table.Th>
                            <Table.Th>Invoice No.</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {parsedOrders.map((o, i) => (
                            <Table.Tr key={`${o.orderNo}-${i}`}>
                              <Table.Td>{i + 1}</Table.Td>
                              <Table.Td>
                                <Text size='xs' fw={600}>
                                  {o.customerName}
                                </Text>
                                {o.customerCity && (
                                  <Text size='10px' c='dimmed'>
                                    {o.customerCity}
                                  </Text>
                                )}
                              </Table.Td>
                              <Table.Td>
                                <Badge
                                  size='xs'
                                  variant='outline'
                                  color={
                                    o.courier.toLowerCase().includes('delhivery')
                                      ? 'blue'
                                      : o.courier.toLowerCase().includes('shadowfax')
                                        ? 'orange'
                                        : o.courier.toLowerCase().includes('valmo')
                                          ? 'teal'
                                          : 'gray'
                                  }
                                >
                                  {o.courier}
                                </Badge>
                              </Table.Td>
                              <Table.Td>
                                <Badge size='xs' variant='light' color='gray'>
                                  {o.destinationCode || '-'}
                                </Badge>
                              </Table.Td>
                              <Table.Td>
                                <Text size='xs' fw={600} c='blue.7'>
                                  {o.sku}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Badge size='xs' variant='filled' color='dark'>
                                  {o.quantity}
                                </Badge>
                              </Table.Td>
                              <Table.Td>
                                <Text size='11px' ff='monospace'>
                                  {o.orderNo || '-'}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size='11px' ff='monospace'>
                                  {o.invoiceNo || '-'}
                                </Text>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </ScrollArea>
                  )}
                </Tabs.Panel>

                {/* Tab 3: Picklist Manifest */}
                <Tabs.Panel value='picklist'>
                  {picklist.length === 0 ? (
                    <Center p='xl' style={{ height: 320 }}>
                      <Text c='dimmed' size='sm'>
                        Upload Meesho PDFs to view warehouse picklist.
                      </Text>
                    </Center>
                  ) : (
                    <Stack gap='sm'>
                      <Alert color='blue' icon={<IconInfoCircle size={16} />}>
                        Use this picklist for warehouse batch picking. Products are aggregated
                        across all uploaded Meesho labels.
                      </Alert>

                      <ScrollArea h={420}>
                        <Table striped highlightOnHover withTableBorder>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>SKU Code</Table.Th>
                              <Table.Th>Total Units to Pack</Table.Th>
                              <Table.Th>Orders Count</Table.Th>
                              <Table.Th>Courier Breakdown</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {picklist.map((item) => (
                              <Table.Tr key={item.sku}>
                                <Table.Td>
                                  <Text size='xs' fw={700} c='blue.8'>
                                    {item.sku}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Badge size='md' variant='filled' color='teal'>
                                    {item.totalQty} unit{item.totalQty !== 1 ? 's' : ''}
                                  </Badge>
                                </Table.Td>
                                <Table.Td>
                                  <Text size='xs' fw={500}>
                                    {item.orderCount} order{item.orderCount !== 1 ? 's' : ''}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Group gap={4}>
                                    {Object.entries(item.couriers).map(([courier, cnt]) => (
                                      <Badge key={courier} size='xs' variant='outline' color='gray'>
                                        {courier}: {cnt}
                                      </Badge>
                                    ))}
                                  </Group>
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </ScrollArea>
                    </Stack>
                  )}
                </Tabs.Panel>

                {/* Tab 4: Saved Batches History */}
                <Tabs.Panel value='history'>
                  <Stack gap='md'>
                    <Group justify='space-between' wrap='wrap' gap='sm'>
                      <TextInput
                        placeholder='Search saved batches by name, customer, SKU, courier...'
                        leftSection={<IconSearch size={16} />}
                        value={historySearch}
                        onChange={(e) => setHistorySearch(e.currentTarget.value)}
                        style={{ flex: 1, minWidth: 260 }}
                      />
                      <Button
                        variant='light'
                        color='gray'
                        size='sm'
                        leftSection={<IconRefresh size={16} />}
                        onClick={() =>
                          void queryClient.invalidateQueries({ queryKey: ['meesho', 'batches'] })
                        }
                        loading={isLoadingBatches}
                      >
                        Refresh
                      </Button>
                    </Group>

                    {isLoadingBatches ? (
                      <Center p='xl' style={{ height: 320, flexDirection: 'column', gap: 12 }}>
                        <Loader color='indigo' size='lg' />
                        <Text size='sm' c='dimmed'>
                          Loading saved batches from server...
                        </Text>
                      </Center>
                    ) : filteredBatches.length === 0 ? (
                      <Center p='xl' style={{ height: 320, flexDirection: 'column', gap: 12 }}>
                        <IconHistory size={48} color='var(--mantine-color-gray-4)' />
                        <Text fw={600} c='dimmed'>
                          {historySearch
                            ? 'No matching saved batches found'
                            : 'No batches saved on the server yet'}
                        </Text>
                        <Text size='xs' c='dimmed' maw={380} ta='center'>
                          {historySearch
                            ? 'Try adjusting your search terms.'
                            : 'Process your Meesho PDFs and click "Save Batch to Server" to preserve your cropped labels and invoices with human-readable tracking.'}
                        </Text>
                      </Center>
                    ) : (
                      <ScrollArea h={480}>
                        <Table striped highlightOnHover withTableBorder verticalSpacing='sm'>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Batch Name & Date</Table.Th>
                              <Table.Th>Configuration</Table.Th>
                              <Table.Th>Orders / Sheets</Table.Th>
                              <Table.Th>SKUs Summary</Table.Th>
                              <Table.Th ta='right'>Actions</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {filteredBatches.map((b) => {
                              const dateStr = new Date(b.createdAt).toLocaleString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              });

                              const uniqueSkus = Array.from(
                                new Set(
                                  (b.orders || [])
                                    .map((o: MeeshoOrderInfo) => o.sku)
                                    .filter(Boolean),
                                ),
                              );

                              return (
                                <Table.Tr key={b.id}>
                                  <Table.Td>
                                    <Text fw={600} size='sm'>
                                      {b.name}
                                    </Text>
                                    <Group gap={6} mt={2}>
                                      <Text size='11px' c='dimmed'>
                                        {dateStr}
                                      </Text>
                                      {b.createdBy?.email && (
                                        <Badge size='xs' variant='light' color='gray'>
                                          {b.createdBy.email.split('@')[0]}
                                        </Badge>
                                      )}
                                    </Group>
                                  </Table.Td>
                                  <Table.Td>
                                    <Group gap={4}>
                                      <Badge size='xs' variant='outline' color='blue'>
                                        {b.layout === '4in1'
                                          ? '4-in-1 A4'
                                          : b.layout === 'thermal_landscape'
                                            ? 'Thermal 6x4'
                                            : b.layout === 'thermal_portrait'
                                              ? 'Thermal 4x6'
                                              : 'Single'}
                                      </Badge>
                                      <Badge
                                        size='xs'
                                        variant='outline'
                                        color={b.invoiceOption === 'skip' ? 'gray' : 'teal'}
                                      >
                                        {b.invoiceOption === 'skip'
                                          ? 'No Invoices'
                                          : '2-in-1 Invoices'}
                                      </Badge>
                                    </Group>
                                  </Table.Td>
                                  <Table.Td>
                                    <Group gap={6}>
                                      <Badge size='sm' variant='filled' color='indigo'>
                                        {b.orderCount} Order{b.orderCount !== 1 ? 's' : ''}
                                      </Badge>
                                      <Text size='xs' c='dimmed'>
                                        ({b.sheetsCount} sheet{b.sheetsCount !== 1 ? 's' : ''})
                                      </Text>
                                    </Group>
                                  </Table.Td>
                                  <Table.Td>
                                    <Group gap={4} maw={240}>
                                      {uniqueSkus.slice(0, 3).map((sku) => (
                                        <Badge key={sku} size='xs' variant='light' color='cyan'>
                                          {sku}
                                        </Badge>
                                      ))}
                                      {uniqueSkus.length > 3 && (
                                        <Text size='10px' c='dimmed'>
                                          +{uniqueSkus.length - 3} more
                                        </Text>
                                      )}
                                    </Group>
                                  </Table.Td>
                                  <Table.Td ta='right'>
                                    <Group justify='flex-end' gap={6}>
                                      <Tooltip label='Load batch into preview & picklist'>
                                        <Button
                                          size='xs'
                                          variant='light'
                                          color='blue'
                                          leftSection={<IconEye size={14} />}
                                          onClick={() => void handleLoadSavedBatch(b.id)}
                                        >
                                          Load
                                        </Button>
                                      </Tooltip>

                                      <Menu position='bottom-end' withArrow shadow='md'>
                                        <Menu.Target>
                                          <ActionIcon variant='subtle' color='gray'>
                                            <IconDotsVertical size={16} />
                                          </ActionIcon>
                                        </Menu.Target>
                                        <Menu.Dropdown>
                                          <Menu.Label>Downloads</Menu.Label>
                                          <Menu.Item
                                            leftSection={<IconFileTypePdf size={14} />}
                                            onClick={() =>
                                              handleDownloadSavedFile(b.id, 'shipping')
                                            }
                                          >
                                            Shipping Labels PDF
                                          </Menu.Item>
                                          {b.hasInvoices && (
                                            <Menu.Item
                                              leftSection={<IconFileText size={14} />}
                                              onClick={() =>
                                                handleDownloadSavedFile(b.id, 'invoice')
                                              }
                                            >
                                              Tax Invoices PDF
                                            </Menu.Item>
                                          )}
                                          {b.hasCombined && (
                                            <Menu.Item
                                              leftSection={<IconDownload size={14} />}
                                              onClick={() =>
                                                handleDownloadSavedFile(b.id, 'combined')
                                              }
                                            >
                                              Combined PDF (Labels + Invoices)
                                            </Menu.Item>
                                          )}
                                          {b.hasZip && (
                                            <Menu.Item
                                              leftSection={<IconArchive size={14} />}
                                              onClick={() => handleDownloadSavedFile(b.id, 'zip')}
                                            >
                                              Full ZIP Package
                                            </Menu.Item>
                                          )}
                                          <Menu.Divider />
                                          <Menu.Item
                                            color='red'
                                            leftSection={<IconTrash size={14} />}
                                            onClick={() =>
                                              void handleDeleteSavedBatch(b.id, b.name)
                                            }
                                          >
                                            Delete Batch
                                          </Menu.Item>
                                        </Menu.Dropdown>
                                      </Menu>
                                    </Group>
                                  </Table.Td>
                                </Table.Tr>
                              );
                            })}
                          </Table.Tbody>
                        </Table>
                      </ScrollArea>
                    )}
                  </Stack>
                </Tabs.Panel>
              </Tabs>
            </Paper>
          </Stack>
        </Grid.Col>
      </Grid>

      {/* Save Batch Modal */}
      <Modal
        opened={saveModalOpened}
        onClose={closeSaveModal}
        title={
          <Group gap='xs'>
            <IconCloudUpload size={20} color='var(--mantine-color-indigo-6)' />
            <Text fw={700} size='md'>
              Save Meesho Batch to Server
            </Text>
          </Group>
        }
        size='md'
        radius='md'
      >
        <Stack gap='md'>
          <TextInput
            label='Batch Name'
            placeholder='Human-readable title'
            value={batchNameInput}
            onChange={(e) => setBatchNameInput(e.currentTarget.value)}
            description='Uses logged-in username, timestamp, and order count for easy tracking'
            required
          />

          <Paper p='sm' radius='md' withBorder bg='gray.0'>
            <Text size='xs' fw={600} c='dimmed' mb='xs'>
              BATCH SUMMARY
            </Text>
            <SimpleGrid cols={2} spacing='xs'>
              <div>
                <Text size='11px' c='dimmed'>
                  Total Orders:
                </Text>
                <Text size='sm' fw={600}>
                  {totalOrders}
                </Text>
              </div>
              <div>
                <Text size='11px' c='dimmed'>
                  Printable Sheets:
                </Text>
                <Text size='sm' fw={600}>
                  {totalSheetsNeeded}
                </Text>
              </div>
              <div>
                <Text size='11px' c='dimmed'>
                  Shipping Layout:
                </Text>
                <Text size='sm' fw={600}>
                  {layout === '4in1' ? '4-in-1 A4' : 'Thermal'}
                </Text>
              </div>
              <div>
                <Text size='11px' c='dimmed'>
                  Invoices:
                </Text>
                <Text size='sm' fw={600}>
                  {invoiceOption === 'skip' ? 'Skipped' : '2-in-1 A4'}
                </Text>
              </div>
            </SimpleGrid>
          </Paper>

          <Group justify='flex-end' gap='xs' mt='sm'>
            <Button variant='subtle' color='gray' onClick={closeSaveModal} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              color='indigo'
              onClick={() => void handleSaveBatch()}
              loading={isSaving}
              leftSection={<IconCloudUpload size={16} />}
            >
              Save & Store Batch
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
